import { SaveOutlined } from '@ant-design/icons';
import { useMutation, useQuery } from '@apollo/client/react';
import { cx } from '@emotion/css';
import { App, Button, Col, Flex, Form, Input, Row, Switch, Tag, Typography } from 'antd';
import { useForm, useWatch } from 'antd/es/form/Form';
import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import type ReactQuill from 'react-quill-new';
import { LoadingIndicator } from '../../../components/LoadingIndicator.tsx';
import { MainContent } from '../../../components/MainContent';
import { RichTextEditor } from '../../../components/RichTextEditor.tsx';
import type { NewsUpdateInput } from '../../../graphql/generated/graphql.ts';
import { PdfPrintPreview } from '../../../PdfPrintPreview.tsx';
import { AttachmentFormItem } from './AttachmentFormItem.tsx';
import { DELETE_NEWS_ATTACHMENT_MUTATION, LAB_NEWS_QUERY, UPDATE_NEWS_MUTATION } from './News.gql';
import { useNewsClassNames } from './News.styles.ts';
import { useAttachmentPreview } from './useAttachmentPreview.ts';

export type AttachmentNumber = 1 | 2 | 3;
type Attachment = {
  attachmentId: string | null | undefined;
  filename: string;
  size: number;
};
type Attachments = {
  [key in AttachmentNumber]: Attachment;
};

const EMPTY_ATTACHMENT = {
  attachmentId: null,
  filename: '',
  size: 0,
};

export const News: React.FC<{ labId: string }> = ({ labId }) => {
  const classNames = useNewsClassNames();
  const [form] = useForm<NewsUpdateInput>();
  const active = useWatch('active', form);
  const { message } = App.useApp();
  const { show: showAttachment, preview } = useAttachmentPreview();
  const [submitAction, setSubmitAction] = useState<'save' | 'saveAndConfirm'>('save');
  const [attachments, setAttachments] = useState<Attachments>({
    '1': EMPTY_ATTACHMENT,
    '2': EMPTY_ATTACHMENT,
    '3': EMPTY_ATTACHMENT,
  });
  const quillRef = useRef<ReactQuill>(null);

  const { data, loading, refetch } = useQuery(LAB_NEWS_QUERY, {
    variables: {
      labId: labId,
    },
  });

  const [updateNewsMutation, { loading: updateLoading }] = useMutation(UPDATE_NEWS_MUTATION);
  const [deleteNewsAttachmentMutation] = useMutation(DELETE_NEWS_ATTACHMENT_MUTATION);

  const update = async (values: NewsUpdateInput, submitAction: string) => {
    // when using enter to submit the form the action is always save, because the html standard uses the first submit button in the DOM
    const confirmationRequired = submitAction === 'saveAndConfirm';

    try {
      await updateNewsMutation({
        variables: {
          input: {
            ...values,
            updateConfirmationId: confirmationRequired,
            labId: labId,
            content:
              // see https://github.com/slab/quill/issues/3957
              quillRef.current?.editor
                ?.getSemanticHTML()
                // see https://github.com/slab/quill/issues/4509
                ?.replace(/((?:&nbsp;)*)&nbsp;/g, '$1 ') ?? values.content,
          },
        },
      });

      message.success('Der News-Eintrag wurde erfolgreich aktualisiert');
      refetch();
    } catch {
      message.error('Der News-Eintrag konnte nicht aktualisiert werden');
    }
  };

  const deleteAttachment = async (number: AttachmentNumber) => {
    try {
      await deleteNewsAttachmentMutation({
        variables: {
          labId: labId,
          number: number,
        },
      });

      updateAttachment(number, EMPTY_ATTACHMENT);
    } catch {
      message.error('Der Anhang konnte nicht entfernt werden');
    }
  };

  const updateAttachment = (number: AttachmentNumber, attachment: Attachment) => {
    setAttachments({
      ...attachments,
      [number]: attachment,
    });
  };

  useEffect(() => {
    if (!data?.labNews) {
      form.setFieldsValue({
        active: false,
        heading: '',
        content: '',
        link1Href: '',
        link1Text: '',
        link2Href: '',
        link2Text: '',
        link3Href: '',
        link3Text: '',
      });
      return;
    }

    form.setFieldsValue({
      active: data.labNews.active,
      heading: data.labNews.heading,
      content: data.labNews.content,
      link1Href: data.labNews.link1Href,
      link1Text: data.labNews.link1Text,
      link2Href: data.labNews.link2Href,
      link2Text: data.labNews.link2Text,
      link3Href: data.labNews.link3Href,
      link3Text: data.labNews.link3Text,
    });

    setAttachments({
      '1': {
        attachmentId: data.labNews.attachment1Id,
        filename: data.labNews.attachment1Filename,
        size: data.labNews.attachment1Size,
      },
      '2': {
        attachmentId: data.labNews.attachment2Id,
        filename: data.labNews.attachment2Filename,
        size: data.labNews.attachment2Size,
      },
      '3': {
        attachmentId: data.labNews.attachment3Id,
        filename: data.labNews.attachment3Filename,
        size: data.labNews.attachment3Size,
      },
    });
  }, [form, data]);

  if (loading) {
    return (
      <MainContent size="small">
        <LoadingIndicator height="200px" />
      </MainContent>
    );
  }

  return (
    <MainContent size="small">
      <Form layout="vertical" form={form} onFinish={values => update(values, submitAction)}>
        <h3 className={classNames.mbLg}>Basisinformationen</h3>
        <Flex wrap className={classNames.colGapLarge}>
          <Form.Item name="heading" label="Überschrift" className={classNames.flexGrow} rules={[{ required: true, whitespace: false }]}>
            <Input placeholder="Geben Sie einen Titel an" />
          </Form.Item>
          <Form.Item
            name="active"
            label="Status"
            extra={
              active ? (
                <Tag className={classNames.statusTag} color="green">
                  veröffentlicht
                </Tag>
              ) : (
                <Tag className={classNames.statusTag} color="red">
                  unveröffentlicht
                </Tag>
              )
            }
            className={classNames.statusFormItem}
          >
            <Switch />
          </Form.Item>
        </Flex>
        <Form.Item
          name="content"
          label="Inhalt"
          rules={[
            {
              required: true,
              validator: () => {
                return !quillRef.current?.editor?.getText()?.trim()?.length
                  ? Promise.reject(new Error('Bitte geben Sie einen Inhalt an'))
                  : Promise.resolve();
              },
            },
          ]}
        >
          <RichTextEditor ref={quillRef} placeholder="Geben Sie den Inhalt an." />
        </Form.Item>

        <h3 className={cx(classNames.mb0, classNames.ptLg)}>Links</h3>
        <div className={classNames.mbLg}>
          <Typography.Text type="secondary">
            Geben Sie bis zu 3 Links an. Wenn Sie keinen Text vergeben, dann wird die URL als Anzeigetext verwendet.
          </Typography.Text>
        </div>
        <Row gutter={[16, 0]}>
          <Col lg={12}>
            <Form.Item name="link1Href" label="Link 1 URL" rules={[{ type: 'url' }]}>
              <Input placeholder="https://www.beispiel.com" />
            </Form.Item>
          </Col>
          <Col lg={12}>
            <Form.Item name="link1Text" label="Link 1 Text">
              <Input placeholder="Link Anzeigetext" />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={[16, 0]}>
          <Col lg={12}>
            <Form.Item name="link2Href" label="Link 2 URL" rules={[{ type: 'url' }]}>
              <Input placeholder="https://www.beispiel.com" />
            </Form.Item>
          </Col>
          <Col lg={12}>
            <Form.Item name="link2Text" label="Link 2 Text">
              <Input placeholder="Link Anzeigetext" />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={[16, 0]}>
          <Col lg={12}>
            <Form.Item name="link3Href" label="Link 3 URL" rules={[{ type: 'url' }]}>
              <Input placeholder="https://www.beispiel.com" />
            </Form.Item>
          </Col>
          <Col lg={12}>
            <Form.Item name="link3Text" label="Link 3 Text">
              <Input placeholder="Link Anzeigetext" />
            </Form.Item>
          </Col>
        </Row>

        <h3 className={cx(classNames.mb0, classNames.ptLg)}>Dateianhänge</h3>
        <div className={classNames.mbLg}>
          <Typography.Text type="secondary">Hochladen von bis zu 3 PDF-Dateien (max. 8 MB pro Datei)</Typography.Text>
        </div>
        <AttachmentFormItem
          labId={labId}
          number={1}
          attachmentId={attachments[1].attachmentId}
          filename={attachments[1].filename}
          size={attachments[1].size}
          onSuccess={result => updateAttachment(1, result)}
          onPreview={() => {
            showAttachment(
              attachments[1].filename,
              `${window._env_.API_URL}/rest/news-attachment/download/${labId}/${attachments[1].attachmentId}`
            );
          }}
          onDelete={() => deleteAttachment(1)}
        />
        <AttachmentFormItem
          labId={labId}
          number={2}
          attachmentId={attachments[2].attachmentId}
          filename={attachments[2].filename}
          size={attachments[2].size}
          onSuccess={result => updateAttachment(2, result)}
          onPreview={() => {
            showAttachment(
              attachments[2].filename,
              `${window._env_.API_URL}/rest/news-attachment/download/${labId}/${attachments[2].attachmentId}`
            );
          }}
          onDelete={() => deleteAttachment(2)}
        />
        <AttachmentFormItem
          labId={labId}
          number={3}
          attachmentId={attachments[3].attachmentId}
          filename={attachments[3].filename}
          size={attachments[3].size}
          onSuccess={result => updateAttachment(3, result)}
          onPreview={() => {
            showAttachment(
              attachments[3].filename,
              `${window._env_.API_URL}/rest/news-attachment/download/${labId}/${attachments[3].attachmentId}`
            );
          }}
          onDelete={() => deleteAttachment(3)}
        />

        <Flex gap="middle" className={classNames.ptLg}>
          <Button htmlType="submit" icon={<SaveOutlined />} disabled={updateLoading} onClick={() => setSubmitAction('save')}>
            Speichern
          </Button>
          <Button
            htmlType="submit"
            type="primary"
            icon={<SaveOutlined />}
            disabled={updateLoading}
            onClick={() => setSubmitAction('saveAndConfirm')}
          >
            Speichern und Lesebestätigung anfordern
          </Button>
        </Flex>
      </Form>
      <PdfPrintPreview {...preview} />
    </MainContent>
  );
};
